import {
  OplItem,
  OplItemStatus,
  OplItemType,
  OplMeeting,
  isOplItemClosed,
} from "src/questionnaires";

/**
 * Presentation helpers for the OPL board. Everything user-facing goes through
 * i18n at the call site; this module only computes, formats and derives.
 */

/**
 * The prefixed helper every OPL component builds over `useAnketaI18n`:
 * `t("status.open", "offen")`. Deliberately NOT the raw i18n signature - taking
 * an options object here is what let a prefix get applied twice.
 */
export type Translate = (key: string, defaultMessage: string) => string;

const dateFmt = new Intl.DateTimeFormat("de-DE", { dateStyle: "short" });
const longFmt = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" });
const timeFmt = new Intl.DateTimeFormat("de-DE", { timeStyle: "short" });

export const fmtDate = (iso: string | null | undefined): string =>
  iso ? dateFmt.format(new Date(iso)) : "-";

export const fmtLongDate = (iso: string | null | undefined): string =>
  iso ? longFmt.format(new Date(iso)) : "-";

export const fmtTime = (iso: string | null | undefined): string =>
  iso ? timeFmt.format(new Date(iso)) : "-";

/** `YYYY-MM-DD`, the shape an <input type="date"> and the API both want. */
export const isoDay = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toISOString().slice(0, 10) : "";

export const typeLabel = (t: Translate, type: OplItemType): string =>
  t(`type.${type}`, type);

export const statusLabel = (t: Translate, status: OplItemStatus): string =>
  t(`status.${status}`, status);

/**
 * Overdue is judged against the round being viewed, not against today: reopening
 * an old round should show what that round saw, not what is late now.
 */
export const isOverdueIn = (item: OplItem, roundDate: string | null): boolean => {
  if (!item.dueDate || isOplItemClosed(item.status)) return false;
  const reference = roundDate ? new Date(roundDate).getTime() : Date.now();
  return new Date(item.dueDate).getTime() < reference;
};

/** Owner shown as the local part-- a column of full addresses is mostly domain. */
export const ownerShort = (owner: string | null): string =>
  owner ? owner.split("@")[0] : "-";

/** The last line of a point's log, which is what the list row previews. */
export const lastLogEntry = (
  item: OplItem,
): { date: string; text: string } | null => {
  if (!item.log?.length) return null;
  const last = item.log[item.log.length - 1];
  return { date: fmtDate(last.entryDate), text: last.text };
};

// --- visibility ------------------------------------------------------------

export type OplBoardView = "meeting" | "closed" | "everything";

/**
 * The concept's hard rule, client side, so the board can switch views without a
 * round trip. Counted in **round ordinals**: a point closed in round n shows
 * once more in n+1 and is hidden from n+2 on.
 *
 * Mirrors functions/opl/agenda.ts - the server is still the one that decides
 * what a round contains; this only drives what the operator is looking at.
 */
export const isVisible = (
  item: OplItem,
  meetings: OplMeeting[],
  currentSequence: number,
  view: OplBoardView,
): boolean => {
  if (view === "everything") return true;
  if (!isOplItemClosed(item.status)) return true;
  if (view === "closed") return true;
  const closedIn = meetings.find((m) => m.id === item.closedInMeetingId);
  if (!closedIn) return true;
  return closedIn.sequence >= currentSequence - 1;
};

// --- Bearbeitungsreihenfolge ----------------------------------------------

export type OplGroupKey = "overdue" | "thisWeek" | "new" | "rest" | "closed";

export const GROUP_ORDER: readonly OplGroupKey[] = [
  "overdue",
  "thisWeek",
  "new",
  "rest",
  "closed",
];

export const groupOf = (
  item: OplItem,
  round: OplMeeting | null,
): OplGroupKey => {
  if (isOplItemClosed(item.status)) return "closed";
  if (!round) return "rest";

  const day = new Date(round.date).getTime();
  const weekOut = day + 7 * 24 * 60 * 60 * 1000;
  const due = item.dueDate ? new Date(item.dueDate).getTime() : null;

  if (due !== null && due < day) return "overdue";
  if (due !== null && due <= weekOut) return "thisWeek";
  if (item.openedInMeetingId === round.id) return "new";
  return "rest";
};

/** The date a week ou- from the round - the "due this week" boundary. */
export const weekBoundary = (round: OplMeeting | null): string => {
  if (!round) return "-";
  const d = new Date(round.date);
  d.setDate(d.getDate() + 7);
  return dateFmt.format(d);
};

/**
 * Everyone in a round, as owner candidates.
 *
 * `value` is what a point stores as its owner: the address when the person has
 * one, their name when they do not. Participants without an account are first
 * class here - they take part and can carry points.
 */
export const peopleOf = (round: OplMeeting | null) =>
  (round?.attendees ?? []).map((a) => ({
    value: (a.email ?? "").trim() || a.name,
    label: a.name,
    email: (a.email ?? "").trim(),
    isGuest: a.isGuest,
    absent: !a.present && !a.isGuest,
    /** Someone who sent a substitute is represented, not absent. */
    represented: !a.present && !!(a.substitute ?? "").trim(),
  }));

/**
 * Owner candidates across the whole board, not just this round: work is often
 * assigned to someone who is not in the room, and the concept allows it - it
 * only insists the assignment be deliberate.
 */
export const allPeople = (meetings: OplMeeting[], round: OplMeeting | null) => {
  const inRound = new Set(peopleOf(round).map((p) => p.value));
  const seen = new Map<string, { value: string; label: string; inRound: boolean }>();
  for (const person of peopleOf(round)) {
    seen.set(person.value, { value: person.value, label: person.label, inRound: true });
  }
  for (const meeting of meetings) {
    for (const a of meeting.attendees) {
      const value = (a.email ?? "").trim() || a.name;
      if (!value || seen.has(value)) continue;
      seen.set(value, { value, label: a.name, inRound: inRound.has(value) });
    }
  }
  return [...seen.values()];
};
